import * as Sentry from "@sentry/react";

import type { RecordingDiagnostics } from "@/components/webcam-capture";
import type { LevelSample } from "@/lib/audio-level";
import type { LevelWindow } from "@/lib/audio-level-windows";
import type { RecordingLevelSummary } from "@/lib/recording-level-summary";

const CATEGORY = "recording";

/** Which half of a dialogue turn a recording belongs to. */
export type RecordingPhase = "preparation" | "response";

/**
 * Sentry helpers for the Socratic recording lifecycle. Breadcrumbs build the
 * trail that gives any later issue context; warnings are the alertable signal
 * that a recording captured or submitted without the expected audio/video.
 *
 * Session Replay masks media (`blockAllMedia`), so these are the only window
 * into why a recording came out degraded.
 */
export function recordingBreadcrumb(
  message: string,
  data?: Record<string, unknown>
): void {
  Sentry.addBreadcrumb({ category: CATEGORY, level: "info", message, data });
}

/** One decimal is as fine as these thresholds are ever read. */
const roundDb = (db: number): number => Math.round(db * 10) / 10;

const levelData = (level: LevelSample) => ({
  rms_db: roundDb(level.rmsDb),
  peak_db: roundDb(level.peakDb),
  silent: level.silent,
});

const windowData = (window: LevelWindow) => ({
  window_index: window.index,
  samples: window.samples,
  level: levelData(window.level),
  context_state: window.contextState,
});

/**
 * Most level crumbs a single dialogue may spend.
 */
export const MAX_AUDIO_LEVEL_CRUMBS = 24;

export interface AudioLevelWindowReporter {
  report: (window: LevelWindow, phase: RecordingPhase) => void;
}

/**
 * The audio level history of a recording
 *
 * One crumb per 10 s window, capped per dialogue so a long conversation cannot
 * push the track-loss and device crumbs out of Sentry's 100-crumb buffer. The
 * full per-window history rides on the silent-recording event, which is where
 * it can be read past the cap.
 */
export function createAudioLevelWindowReporter(
  dialogueId: string | undefined
): AudioLevelWindowReporter {
  let reported = 0;

  return {
    report(window, phase) {
      if (reported >= MAX_AUDIO_LEVEL_CRUMBS) return;
      reported += 1;

      if (reported === MAX_AUDIO_LEVEL_CRUMBS) {
        // Say that the trail stops here, so a reader does not take the last
        // crumb as the state the recording ended in.
        recordingBreadcrumb("audio levels capped", {
          dialogue_id: dialogueId,
          reported_windows: reported,
        });
        return;
      }

      recordingBreadcrumb("audio levels", {
        phase,
        dialogue_id: dialogueId,
        ...windowData(window),
      });
    },
  };
}

export function captureRecordingWarning(
  message: string,
  tags: Record<string, string | number | boolean>,
  extra: Record<string, unknown>
): void {
  Sentry.captureMessage(message, { level: "warning", tags, extra });
}

const levelSummaryExtra = (
  summary: RecordingLevelSummary | null
): Record<string, unknown> => {
  if (summary == null) return { audio_levels: "not_measured" };

  return {
    silent_window_fraction:
      Math.round(summary.silentWindowFraction * 100) / 100,
    windows: summary.windows,
    throttled_windows: summary.throttledWindows,
    stopped_graph_windows: summary.stoppedGraphWindows,
    silent_windows: summary.silentWindows,
    level: summary.level == null ? null : levelData(summary.level),
  };
};

const windowHistoryExtra = (summary: RecordingLevelSummary) => ({
  window_history: summary.history.map(windowData),
});

/** The raw stream's tracks, in the shape existing Sentry searches read. */
const rawTrackSummary = (diagnostics: RecordingDiagnostics) => ({
  audio: diagnostics.microphone,
  video: diagnostics.camera.atCapture,
});

/** What both completion warnings report on: one finished recording. */
interface CompletedRecordingReport {
  dialogueId: string | undefined;
  durationSeconds: number;
  diagnostics: RecordingDiagnostics;
}

export interface SilentRecordingReport extends CompletedRecordingReport {
  phase: RecordingPhase;
  /** Whether the student was shown the "we can't hear you" nudge. */
  nudgeShown: boolean;
}

/**
 * A recording whose audio was silent for effectively all of its length.∑
 */
export function captureSilentRecordingWarning({
  phase,
  dialogueId,
  durationSeconds,
  diagnostics,
  nudgeShown,
}: SilentRecordingReport): void {
  const summary = diagnostics.levelSummary;
  if (summary == null || summary.silent !== true) return;

  const { audioDevice } = diagnostics;

  captureRecordingWarning(
    "Socratic recording submitted silent",
    {
      phase,
      nudge_shown: nudgeShown,
      context_state: diagnostics.audioContextState,
      track_muted: diagnostics.microphone.muted ?? "unknown",
      capture_surface: "socratic",
    },
    {
      dialogue_id: dialogueId,
      duration_seconds: durationSeconds,
      ...levelSummaryExtra(summary),
      ...windowHistoryExtra(summary),
      // track_settings carries groupId and channelCount, so neither is repeated.
      device_label: audioDevice.label,
      track_settings: audioDevice.settings,
      track_summary: rawTrackSummary(diagnostics),
    }
  );
}

export type DegradedRecordingReport = CompletedRecordingReport;

/**
 * A recording that is missing a track it should have carried. Structural
 * failures only.
 */
export function captureDegradedRecordingWarning({
  dialogueId,
  durationSeconds,
  diagnostics,
}: DegradedRecordingReport): void {
  const { recordedAudio, camera } = diagnostics;
  const degraded =
    recordedAudio.outcome !== "present" ||
    camera.outcome === "lost_mid_recording" ||
    camera.outcome === "missing_at_start";
  if (!degraded) return;

  captureRecordingWarning(
    "Socratic recording submitted degraded",
    {
      audio_outcome: recordedAudio.outcome,
      video_outcome: camera.outcome,
    },
    {
      dialogue_id: dialogueId,
      duration_seconds: durationSeconds,
      ...levelSummaryExtra(diagnostics.levelSummary),
      track_summary: rawTrackSummary(diagnostics),
      recorded_audio_summary: recordedAudio.atCapture,
    }
  );
}

/**
 * Report a recording upload failure as an exception
 */
export function captureRecordingError(
  error: unknown,
  tags: Record<string, string | number | boolean>,
  extra: Record<string, unknown>
): void {
  Sentry.captureException(error, { level: "error", tags, extra });
}
